import React from "react";
import axios from "axios";
import { ChevronDown, Check, X } from "lucide-react";
import { API, LineupPill, StatusPill, pct } from "@/components/fbShared";

const TIER_CLS = { "APPROVED PAIR": "border-emerald-500/50 text-emerald-300", CANDIDATE: "border-cyan-700/60 text-cyan-300", "INCLUDES PASS": "border-slate-700 text-slate-400" };

const Leg = ({ l, o, onPlayer, testId }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px]" data-testid={testId}>
    <button type="button" onClick={() => onPlayer(l.id)} className="font-bold text-slate-100 hover:text-orange-300">{l.name}</button>
    <span className="text-slate-500">{l.team} {l.pos} · {l.matchup}</span>
    <span className="text-orange-300">{pct(l.prob)}</span>
    <span className="text-slate-500">Fusion {l.fusion ?? "N/A"}</span>
    <StatusPill s={l.status} />
    <LineupPill s={l.starter} />
    {o && (o.hit ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <span className="flex items-center gap-1 text-rose-400"><X className="h-3.5 w-3.5" />{o.first_scorer}</span>)}
  </div>
);

const Detail = ({ p }) => (
  <div className="mt-2 space-y-1 rounded-lg border border-slate-800 bg-slate-950/60 p-2 font-mono text-[10px] text-slate-400" data-testid={`fb-pair-detail-${p.rank}`}>
    <p>Illustrative combined probability: {pct(p.a.prob)} × {pct(p.b.prob)} = <b className="text-slate-200">{pct(p.indep_prob, 2)}</b> — independence-based estimate, not a validated joint probability and not used for ranking.</p>
    {[p.a, p.b].map((l) => (
      <p key={l.id}>{l.name}: team opening-possession {pct(l.tip_edge)}{l.strong_tip ? " (strong tip ≥70%)" : ""}{l.weak_tip_target ? ` · opponent weak on the tip (${pct(l.opp_tip)})` : ""} · {l.starts ?? "N/A"} starts · FB rate {l.fb_rate == null ? "N/A" : `${l.fb_rate}%`}{l.warnings.length ? ` · ⚠ ${l.warnings.join(", ")}` : ""}</p>
    ))}
    <p>{p.a.name}: {p.a.status_reason} · {p.b.name}: {p.b.status_reason}</p>
  </div>
);

const Row = ({ p, onPlayer }) => {
  const [open, setOpen] = React.useState(false);
  const o = p.outcome;
  return (
    <div className="border-t border-slate-800/70 py-2 first:border-t-0" data-testid={`fb-pair-${p.rank}`}>
      <div className="flex items-start gap-2">
        <span className="w-5 pt-0.5 font-mono text-xs font-bold text-slate-600">{p.rank}</span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-display text-[13px] font-bold text-slate-100">{p.a.name} + {p.b.name}
            <span className={`ml-2 rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase ${TIER_CLS[p.tier]}`} data-testid={`fb-pair-tier-${p.rank}`}>{p.tier}</span>
            <span className="ml-2 rounded border border-orange-800/60 px-1.5 py-0.5 font-mono text-[9px] uppercase text-orange-300" data-testid={`fb-pair-group-${p.rank}`}>G{p.group} · {p.group_label}</span>
            {o && <span className={`ml-2 font-mono text-[10px] ${o.both ? "text-emerald-400" : "text-slate-500"}`}>{o.both ? "BOTH HIT" : `${o.legs_hit}/2 legs`}</span>}
          </p>
          <Leg l={p.a} o={o?.a} onPlayer={onPlayer} testId={`fb-pair-${p.rank}-a`} />
          <Leg l={p.b} o={o?.b} onPlayer={onPlayer} testId={`fb-pair-${p.rank}-b`} />
          {open && <Detail p={p} />}
        </div>
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`fb-pair-toggle-${p.rank}`} className="rounded p-1 text-slate-500 hover:text-orange-300">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
    </div>
  );
};

export const PairsBoard = ({ date, onPlayer, stamp }) => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    setD(null);
    axios.get(`${API}/pairs`, { params: { date } }).then((r) => live && setD(r.data)).catch(() => live && setD({ error: true }));
    return () => { live = false; };
  }, [date, stamp]);
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid="fb-pairs">
      <p className="font-display text-[13px] font-bold uppercase text-orange-200">Top 10 first-basket pairs</p>
      {!d ? <p className="font-mono text-[10px] text-slate-500">Loading pairs…</p>
        : d.error ? <p className="font-mono text-[10px] text-rose-400" data-testid="fb-pairs-error">Pairs unavailable.</p>
        : (
          <>
            <p className="mb-1 font-mono text-[9px] text-slate-500" data-testid="fb-pairs-meta">
              {d.pool} eligible players across {d.games} games ({d.pool_scope}){d.excluded ? ` · ${d.excluded} DATA BLOCKED excluded` : ""}{d.below_min ? ` · ${d.below_min} below 12%` : ""} · built {new Date(d.built_at).toLocaleTimeString()}
              {d.frozen?.frozen_at ? ` · frozen for audit ${new Date(d.frozen.frozen_at).toLocaleString()}${d.frozen.locked ? " (locked)" : ""}` : ""}
            </p>
            <p className="mb-2 font-mono text-[9px] text-slate-600">{d.rule}</p>
            {d.retro && <p className="mb-2 font-mono text-[10px] text-amber-300" data-testid="fb-pairs-retro">Retrospective view built with today’s model, which may already include these games’ results, so it is not a pre-game prediction. Frozen boards are graded in the Audit tab.</p>}
            {!d.pairs.length ? <p className="font-mono text-[10px] text-slate-500" data-testid="fb-pairs-empty">No valid cross-game pairs for this date.</p>
              : d.pairs.map((p) => <Row key={p.pair_id} p={p} onPlayer={onPlayer} />)}
          </>
        )}
    </div>
  );
};

export const PairsAudit = () => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { axios.get(`${API}/pairs/audit`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  if (!d) return null;
  if (d.error) return <p className="font-mono text-[10px] text-rose-400">Pair audit unavailable.</p>;
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid="fb-pairs-audit">
      <p className="font-display text-[13px] font-bold uppercase text-orange-200">Pairs audit · frozen boards</p>
      <p className="mb-2 font-mono text-[10px] text-slate-400" data-testid="fb-pairs-audit-summary">
        {d.boards} boards · {d.pairs_graded} pairs graded ({d.pending} pending) · both legs hit {d.both_hit} (independence estimate {d.expected_both}) · leg hit rate {d.leg_hit_rate == null ? "N/A" : `${d.leg_hit_rate}%`}
      </p>
      {Object.entries(d.by_tier).map(([t, v]) => (
        <p key={t} className="font-mono text-[10px] text-slate-500">{t}: {v.both}/{v.pairs} both hit · legs {v.legs_hit}/{v.legs} · expected both {v.expected_both}</p>
      ))}
      <div className="mt-2 max-h-72 overflow-auto">
        {d.rows.map((r, i) => (
          <p key={i} className="border-t border-slate-800/60 py-1 font-mono text-[10px] text-slate-400" data-testid={`fb-pairs-audit-row-${i}`}>
            {r.date} #{r.rank} {r.a} ({pct(r.a_prob)}, F {r.a_fusion}) + {r.b} ({pct(r.b_prob)}, F {r.b_fusion}) · {r.tier} ·{" "}
            {r.result ? <b className={r.result.both ? "text-emerald-400" : "text-slate-300"}>{r.result.both ? "BOTH HIT" : `${r.result.legs_hit}/2`}</b> : "pending"}
          </p>
        ))}
      </div>
      <p className="mt-2 font-mono text-[9px] text-slate-600">{d.definitions.both} {d.definitions.freeze}</p>
    </div>
  );
};
